import { type JSX, useState, useEffect } from 'react';
import Typography from 'carbon-react/esm/components/typography';
import Box from 'carbon-react/esm/components/box';
import Button from 'carbon-react/esm/components/button/__next__';
import { Checkbox } from 'carbon-react/esm/components/checkbox';
import Dialog from 'carbon-react/esm/components/dialog';
import Textbox from 'carbon-react/esm/components/textbox';
import { Select, Option } from 'carbon-react/esm/components/select';
import { Collaborator, EmptyCollaborator } from '../../../../types/collaborator.types';
import { useProfilesContext } from '../../../../context/useProfilesContext';
import {
  collaboratorFormSchema,
  type CollaboratorFormValues,
} from '../../../../types/schemas/collaborator.schema';

interface AddCollaboratorDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    newCollaborator: Collaborator,
    confirmEmail: string,
  ) => Promise<{ success: boolean; error?: string }>;
  onSuccess?: (message: string) => void;
  onError?: (error: unknown) => void;
}

const AddCollaboratorDialog = ({
  isOpen,
  onClose,
  onSave,
  onSuccess,
  onError,
}: AddCollaboratorDialogProps): JSX.Element => {
  const { profiles, refetchProfiles } = useProfilesContext();
  const [isLoading, setIsLoading] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const [formData, setFormData] = useState<Collaborator>(EmptyCollaborator);
  const [apiError, setApiError] = useState('');

  // sélectionne le profil par défaut lors du chargement des profils
  useEffect(() => {
    if (profiles.length > 0) {
      const defaultProfile = profiles.find(p => p.default) || profiles[0];
      setFormData(prev => ({ ...prev, profileId: defaultProfile.id }));
    }
  }, [profiles]);

  // Réinitialiser le formulaire à la fermeture de la dialog
  useEffect(() => {
    if (!isOpen) {
      setFormData(EmptyCollaborator);
      setConfirmEmail('');
      setErrors({ lastName: '', email: '', confirmEmail: '' });
      setApiError('');
      if (profiles.length > 0) {
        const defaultProfile = profiles.find(p => p.default) || profiles[0];
        setFormData(prev => ({ ...prev, profileId: defaultProfile.id }));
      }
    }
  }, [isOpen, profiles]);

  // États pour les erreurs de validation
  const [errors, setErrors] = useState({
    lastName: '',
    email: '',
    confirmEmail: '',
  });

  // Fonction de validation avec Zod
  const validateForm = (): CollaboratorFormValues | null => {
    const formDataToValidate = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      confirmEmail,
      profileId: formData.profileId,
      isActive: formData.isActive,
    };

    const result = collaboratorFormSchema.safeParse(formDataToValidate);

    if (result.success) {
      setErrors({ lastName: '', email: '', confirmEmail: '' });
      return result.data;
    }

    const newErrors = { lastName: '', email: '', confirmEmail: '' };
    result.error.issues.forEach(issue => {
      const field = issue.path[0] as keyof typeof newErrors;
      if (field in newErrors) {
        newErrors[field] = issue.message;
      }
    });
    setErrors(newErrors);
    return null;
  };

  const handleChange = <K extends keyof Collaborator>(field: K, value: Collaborator[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setApiError('');

    // Réinitialiser l'erreur du champ modifié
    if (field === 'lastName' || field === 'email') {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleConfirmEmailChange = (value: string) => {
    setConfirmEmail(value);
    setApiError('');
    setErrors(prev => ({ ...prev, confirmEmail: '' }));
  };

  const handleSubmit = async () => {
    // Valider le formulaire avant la soumission
    const validatedForm = validateForm();
    if (!validatedForm) {
      return;
    }

    try {
      setIsLoading(true);
      const normalizedFormData: Collaborator = {
        ...formData,
        firstName: validatedForm.firstName ?? '',
        lastName: validatedForm.lastName,
        email: validatedForm.email,
        profileId: validatedForm.profileId,
        isActive: validatedForm.isActive,
      };
      const { success, error } = await onSave(normalizedFormData, validatedForm.confirmEmail);
      if (success) {
        await refetchProfiles();
        const successMessage = `Le collaborateur ${normalizedFormData.firstName} ${normalizedFormData.lastName} a été ajouté avec succès`;

        onClose();
        onSuccess?.(successMessage);
      } else {
        const errorMessage = error || "Une erreur est survenue lors de l'ajout du collaborateur";
        setApiError(errorMessage);
        onError?.(new Error(errorMessage));
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "Une erreur est survenue lors de l'ajout du collaborateur";
      setApiError(errorMessage);
      onError?.(err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onCancel={onClose} title="Ajouter un collaborateur">
        <Box display={'flex'} flexDirection={'column'} gap={3} mb={3}>
          <Box display={'flex'} flexDirection={'column'} gap={2} mb={3}>
            <Typography variant="h3">Informations</Typography>
            <Textbox
              size="small"
              label="Prénom"
              value={formData.firstName}
              onChange={e => handleChange('firstName', e.target.value)}
            />
            <Textbox
              size="small"
              required
              label="Nom"
              value={formData.lastName}
              onChange={e => handleChange('lastName', e.target.value)}
              error={errors.lastName}
            />
            <Textbox
              size="small"
              required
              label="Email"
              value={formData.email}
              onChange={e => handleChange('email', e.target.value)}
              error={errors.email}
            />
            <Textbox
              size="small"
              required
              label="Confirmer l'email"
              value={confirmEmail}
              onChange={e => handleConfirmEmailChange(e.target.value)}
              error={errors.confirmEmail}
            />
          </Box>

          <Box display={'flex'} flexDirection={'column'} gap={3} mb={2}>
            <Typography variant="h3">Accès</Typography>
            <Select
              label="Profil"
              value={formData.profileId}
              onChange={e => handleChange('profileId', e.target.value)}
            >
              {profiles.map(profile => (
                <Option key={profile.id} text={profile.name} value={profile.id}>
                  {profile.name}
                </Option>
              ))}
            </Select>
            <Checkbox
              label="Actif"
              checked={formData.isActive}
              onChange={() => handleChange('isActive', !formData.isActive)}
            />
          </Box>
        </Box>

        {apiError && (
          <Box mb={2}>
            <Typography variant="p">{apiError}</Typography>
          </Box>
        )}

        <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
          <Button
            onClick={onClose}
            variantType="secondary"
            size="small"
            mr={2}
            disabled={isLoading}
          >
            Annuler
          </Button>

          <Button onClick={handleSubmit} variantType="primary" size="small" disabled={isLoading}>
            {isLoading ? 'Enregistrement...' : 'Valider'}
          </Button>
        </Box>
      </Dialog>
    </>
  );
};

export default AddCollaboratorDialog;
